import React, { useEffect, useState } from "react";
import { Form, Input, Select, Button } from "antd";
import {
  UserOutlined,
  LockOutlined,
  EyeTwoTone,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../store/authStore";
import { toast } from "react-toastify";
import { rolesApi } from "../services/api";

const Login = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  // Usar el store de autenticación
  const {
    login,
    isAuthenticated,
    loading,
    clearMessages,
    clearSession,
    error,
  } = useAuthStore();

  const initialValues = {
    email: "",
    password: "",
    rol_id: undefined,
  };

  // Cargar roles desde la API
  useEffect(() => {
    const fetchRoles = async () => {
      setLoadingRoles(true);
      try {
        const response = await rolesApi.getAll();
        if (response.data && response.data.status && response.data.roles) {
          setRoles(response.data.roles);
        }
      } catch (error) {
        console.error("Error al cargar roles:", error);
        toast.error("No se pudieron cargar los tipos de usuario");
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchRoles();
  }, []);

  // Redireccionar si ya está autenticado
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/novedades");
    }
    // Limpiar mensajes al montar/desmontar el componente
    return () => clearMessages();
  }, [isAuthenticated, navigate, clearMessages]); // Ya no necesitamos este efecto ya que los errores se muestran en el store
  // Los toast ya se manejan en el store de autenticación

  const onFinish = async (values) => {
    try {
      // Preparar los datos en el formato que espera la API
      const credentials = {
        email: values.email,
        password: values.password,
        rol_id: parseInt(values.rol_id),
      };

      // Usar la acción login del store - las notificaciones se manejan en el store
      const result = await login(credentials);

      if (!result.status) {
        toast.error(result.message || "Error al iniciar sesión");
      }
    } catch (error) {
      console.error("Error durante el login:", error);
      toast.error("Error al conectar con el servidor");
    }
  };

  return (
    <section className="h-screen bg-gray-200">
      <div className="w-[90%] md:w-[50%] mx-auto h-full">
        <div className="flex items-center justify-center h-full">
          <div className="w-full md:w-4/5 lg:w-3/4 xl:w-2/3 rounded-lg shadow-md bg-white">
            <div className="p-4 md:p-8">
              <div className="flex mb-8">
                <img
                  className="mx-auto my-auto w-[70%]"
                  src="http://sievi.udi.edu.co/sievi/img/logo-udi-negro.bbe5273e.png"
                  alt="Logo UDI"
                />
              </div>

              <div>
                <h3 className="text-left mx-4 mb-1 font-semibold text-sm">
                  Software de Novedades Administrativas y Docentes.
                </h3>
              </div>

              <Form
                form={form}
                name="login"
                onFinish={onFinish}
                className="mx-4 my-2"
                layout="vertical"
                initialValues={initialValues}
              >
                <Form.Item
                  label="Correo electrónico"
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: "Por favor ingrese su correo electrónico",
                    },
                    {
                      type: "email",
                      message: "Ingrese un correo electrónico válido",
                    },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="Correo electrónico"
                  />
                </Form.Item>
                <Form.Item
                  label="Contraseña"
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: "Por favor ingrese su contraseña",
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Contraseña"
                    iconRender={(visible) =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>
                <Form.Item
                  label="Tipo de usuario"
                  name="rol_id"
                  rules={[
                    {
                      required: true,
                      message: "Por favor seleccione el tipo de usuario",
                    },
                  ]}
                >
                  <Select
                    placeholder="Seleccione el tipo de usuario"
                    loading={loadingRoles}
                  >
                    {roles.map((rol) => (
                      <Select.Option key={rol.id} value={rol.id.toString()}>
                        {rol.rol}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>{" "}
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="w-full bg-neutral-900 hover:bg-neutral-800 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                    icon={<UserOutlined />}
                    loading={loading}
                  >
                    Iniciar sesión
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Login;
